<template>
    <v-dialog v-model="visible" max-width="650" persistent>
        <v-card>
            <!-- Header -->
            <div class="dialog-header d-flex align-center justify-space-between px-6 py-3">
                <span class="dialog-title">{{ $t("esgPlatform.esgReport.templateSettings") }}</span>
                <v-btn icon @click="close" class="white--text">
                    <v-icon medium>mdi-close</v-icon>
                </v-btn>
            </div>
            <v-card-text class="px-6 py-4">
                <!-- Main Colors/Font -->
                <v-row>
                    <v-col cols="12" md="6">
                        <div class="setting-label">{{ $t("esgPlatform.esgReport.backgroundColor") }}</div>
                        <v-select
                            v-model="selectedBgColor"
                            :items="themeColors"
                            item-text="name"
                            item-value="value"
                            outlined
                            hide-details
                            class="color-select"
                            @change="onBgColorChange"
                        >
                            <template v-slot:prepend-inner>
                                <span class="color-box" :style="{ background: local.bgColor }"></span>
                            </template>
                        </v-select>

                        <v-text-field
                            v-if="selectedBgColor === 'custom'"
                            v-model="local.bgColor"
                            outlined
                            hide-details
                            class="color-input mt-2"
                            label="Custom HEX Color"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <div class="setting-label">{{ $t("esgPlatform.esgReport.fontColor") }}</div>
                        <v-select
                            v-model="selectedFontColor"
                            :items="themeColors"
                            item-text="name"
                            item-value="value"
                            outlined
                            hide-details
                            class="color-select"
                            @change="onFontColorChange"
                        >
                            <template v-slot:prepend-inner>
                                <span class="color-box" :style="{ background: local.fontColor }"></span>
                            </template>
                        </v-select>

                        <v-text-field
                            v-if="selectedFontColor === 'custom'"
                            v-model="local.fontColor"
                            outlined
                            hide-details
                            class="color-input mt-2"
                            label="Custom HEX Color"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <div class="setting-label">{{ $t("esgPlatform.esgReport.font") }}</div>
                        <v-select v-model="local.font" :items="fonts" outlined hide-details />
                    </v-col>
                </v-row>
                <v-divider class="my-6" />
                <!-- PDF Header -->
                <div class="pdf-header-label mb-2">{{ $t("esgPlatform.esgReport.pdfHeader") }}</div>
                <v-row>
                    <v-col cols="12" md="6">
                        <div class="setting-label">{{ $t("esgPlatform.esgReport.backgroundColor") }}</div>
                        <v-select
                            v-model="selectedPdfHeaderBg"
                            :items="themeColors"
                            item-text="name"
                            item-value="value"
                            outlined
                            hide-details
                            class="color-select"
                            @change="onPdfHeaderBgChange"
                        >
                            <template v-slot:prepend-inner>
                                <span class="color-box" :style="{ background: local.pdfHeaderBg }"></span>
                            </template>
                        </v-select>
                        <v-text-field
                            v-if="selectedPdfHeaderBg === 'custom'"
                            v-model="local.pdfHeaderBg"
                            outlined
                            hide-details
                            class="color-input mt-2"
                            label="Custom HEX Color"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <div class="setting-label">{{ $t("esgPlatform.esgReport.fontColor") }}</div>
                        <v-select
                            v-model="selectedPdfHeaderFontColor"
                            :items="themeColors"
                            item-text="name"
                            item-value="value"
                            outlined
                            hide-details
                            class="color-select"
                            @change="onPdfHeaderFontColorChange"
                        >
                            <template v-slot:prepend-inner>
                                <span class="color-box" :style="{ background: local.pdfHeaderFontColor }"></span>
                            </template>
                        </v-select>
                        <v-text-field
                            v-if="selectedPdfHeaderFontColor === 'custom'"
                            v-model="local.pdfHeaderFontColor"
                            outlined
                            hide-details
                            class="color-input mt-2"
                            label="Custom HEX Color"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <div class="setting-label">{{ $t("esgPlatform.esgReport.font") }}</div>
                        <v-select v-model="local.pdfHeaderFont" :items="fonts" outlined hide-details />
                    </v-col>
                </v-row>
                <v-divider class="my-6" />
                <!-- Report Header Logo -->
                <div class="pdf-header-label mb-2">{{ $t("esgPlatform.esgReport.reportHeaderLogo") }}</div>
                <div class="setting-label mb-1">{{ $t("esgPlatform.esgReport.uploadLogo") }}</div>
                <div class="logo-upload-box mb-4" @click="triggerFileInput">
                    <v-icon size="40" color="#bdbdbd">mdi-camera</v-icon>
                    <div class="add-image-btn">{{ $t("esgPlatform.esgReport.addImage") }}</div>
                    <input ref="fileInput" type="file" accept="image/*" class="d-none" @change="onLogoChange" />
                </div>
                <v-col cols="12">
                    <!-- Progress bar section -->
                    <v-row v-if="isUploading" align="center" justify="center">
                        <v-col cols="12" class="text-center">
                        <p>{{ $t("esgPlatform.esgReport.pleaseWait") }}</p>
                        <v-progress-linear :value="uploadPercentage" height="7" color="primary" />
                        <p>{{ uploadPercentage }}%</p>
                        </v-col>
                    </v-row>
                </v-col>
                <div class="setting-label mb-1">{{ $t("esgPlatform.esgReport.defaultLogo") }}</div>
                <v-img v-if="local.logo" :src="local.logo" max-width="220" max-height="60" contain class="mb-2" />
                <v-img v-else src="https://www.abrafrutas.org/wp-content/uploads/2021/07/Logo-Abrafrutas-2021.png"
                    max-width="220" max-height="60" contain class="mb-2" />
            </v-card-text>
            <v-card-actions class="justify-end px-6 pb-6 pt-4">
                <v-btn outlined color="primary" class="mr-4" @click="close">{{ $t("esgPlatform.esgReport.cancel") }}</v-btn>
                <v-btn color="primary"  @click="save">{{ $t("esgPlatform.esgReport.save") }}</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import S3UploadService from "@/_services/S3UploadService";

export default {
    props: {
        value: Boolean,
        settings: Object
    },
    data() {
        return {
            isUploading: false,
            uploadPercentage: 0,
            visible: false,
            local: {
                bgColor: '#ffffff',
                fontColor: '#101010',
                font: 'Poppins',
                pdfHeaderBg: '#244513',
                pdfHeaderFontColor: '#ffffff',
                pdfHeaderFont: 'Poppins',
                logo: ''
            },
            themeColors: [
                { name: this.$t('esgPlatform.esgReport.colorWhite'), value: "#ffffff" },
                { name: this.$t('esgPlatform.esgReport.colorBlue'), value: "#1A5685"},
                { name: this.$t('esgPlatform.esgReport.colorPrimaryGreen'), value: "#117646" }, 
                { name: this.$t('esgPlatform.esgReport.colorDarkGreen'), value: "#244513" },   
                { name: this.$t('esgPlatform.esgReport.colorBlack'), value: "#101010" },
                { name: this.$t('esgPlatform.esgReport.colorGrey'), value: "#bdbdbd" },
                { name: this.$t('esgPlatform.esgReport.colorCustom'), value: "custom" }
            ],
            selectedBgColor: "#ffffff",
            selectedFontColor: "#101010",
            selectedPdfHeaderBg: "#244513",
            selectedPdfHeaderFontColor: "#ffffff",
            fonts: [
                'Poppins', 'Arial', 'Roboto', 'Oswald', 'Georgia', 'Times New Roman', 'Helvetica'
            ]
        }
    },
    watch: {
        value(val) {
            this.visible = val
            if (val && this.settings) {
                this.local = { ...this.local, ...this.settings }
                this.selectedBgColor = this.themeColors.find(tc => tc.value === this.local.bgColor) ? this.local.bgColor : 'custom';
                this.selectedFontColor = this.themeColors.find(tc => tc.value === this.local.fontColor) ? this.local.fontColor : 'custom';
                // For PDF Header colors:
                this.selectedPdfHeaderBg = this.themeColors.find(tc => tc.value === this.local.pdfHeaderBg) ? this.local.pdfHeaderBg : 'custom';
                this.selectedPdfHeaderFontColor = this.themeColors.find(tc => tc.value === this.local.pdfHeaderFontColor) ? this.local.pdfHeaderFontColor : 'custom';
            }
        }
    },
    methods: {
        close() {
            this.$emit('input', false)
        },
        save() {
            this.$emit('save', { ...this.local })
            this.close()
        },
        triggerFileInput() {
            this.$refs.fileInput.click()
        },
        async onLogoChange(e) {
            const file = e.target.files[0];
            const folder = "dimitra-public-images";
            const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
            if (!file) return;
            if (!allowedTypes.includes(file.type)) {
                this.$notify({
                    title: this.$t("esgPlatform.esgReport.fileUploadFailed") || "Upload Failed",
                    text: this.$t("esgPlatform.esgReport.unsupportedFileFormat") || "Unsupported file format",
                    type: "error",
                });
                e.target.value = '';
                return;
            }
 
            try {
                this.isUploading = true;
                const result = await S3UploadService.imageUpload(file, folder, (progress) => {
                    this.uploadPercentage = progress;
                });
                if (result && result.fullPath) {
                    if (this.local) {
                        this.local.logo = result.fullPath;
                    } else {
                        this.$set(this.settings, 'logo', result.fullPath);
                    }
                }
 
            } catch (err) {
                console.error(err);
                this.$notify({
                    title: this.$t("esgPlatform.esgReport.fileUploadFailed") || "Upload Failed",
                    text: this.$t("esgPlatform.esgReport.errorUploadingFile") || "Error uploading file",
                    type: "error",
                });
            } finally {
                this.isUploading = false;
                this.uploadPercentage = 0;
                e.target.value = '';
            }
        },
        onBgColorChange(value) {
            if (value === 'custom') {
                if (!this.themeColors.find(tc => tc.value === this.local.bgColor)) {
                    this.local.bgColor = '#ffffff';
                }
            } else {
                this.local.bgColor = value;
            }
        },
        onFontColorChange(value) {
            if (value === 'custom') {
                if (!this.themeColors.find(tc => tc.value === this.local.fontColor)) {
                    this.local.fontColor = '#101010';
                }
            } else {
                this.local.fontColor = value;
            }
        },
        onPdfHeaderBgChange(value) {
            if (value === 'custom') {
                if (!this.themeColors.find(tc => tc.value === this.local.pdfHeaderBg)) {
                    this.local.pdfHeaderBg = "#244513";
                }
            } else {
                this.local.pdfHeaderBg = value;
            }
        },
        onPdfHeaderFontColorChange(value) {
            if (value === 'custom') {
                if (!this.themeColors.find(tc => tc.value === this.local.pdfHeaderFontColor)) {
                    this.local.pdfHeaderFontColor = "#ffffff";
                }
            } else {
                this.local.pdfHeaderFontColor = value;
            }
        }
    }
}
</script>

<style scoped>
.dialog-header {
    background: #117646;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
}

.dialog-title {
    color: #fff;
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: 0.5px;
}

.setting-label {
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 8px;
    color: #222;
}

.pdf-header-label {
    font-size: 16px;
    font-weight: 700;
    color: #222;
    margin-bottom: 8px;
}

.color-input {
    font-size: 1rem;
}

.color-box {
    display: inline-block;
    width: 28px;
    height: 28px;
    border-radius: 4px;
    border: 1.5px solid #d1d1d1;
    margin-right: 12px;
    vertical-align: middle;
}

.logo-upload-box {
    border: 2px dashed #bdbdbd;
    border-radius: 8px;
    padding: 24px 0 12px 0;
    text-align: center;
    cursor: pointer;
    color: #888;
    background: #fff;
    margin-bottom: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.add-image-btn {
    margin-top: 8px;
    background: #4ecb8f;
    color: #fff;
    border-radius: 4px;
    padding: 6px 22px;
    font-size: 16px;
    box-shadow: 0 2px 4px #0001;
}

.d-none {
    display: none;
}
</style>